import {Localized, useLocalization} from "@fluent/react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {Box, Typography, useTheme} from "@mui/material";
import {SimpleTreeView} from "@mui/x-tree-view";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useHistory, useRouteMatch} from "react-router";

import parseDate from "../../helpers/parseDate";
import {
	getChapterPathByKey,
	getSectionPathByKey,
} from "../../helpers/pathHelpers";
import {scrollToTop} from "../../helpers/scrollHelpers";
import type Chapter from "../../store/chapters/Chapter";
import {requiresExplicitSessionStart} from "../../store/chapters/Chapter";
import selectChapter from "../../store/chapters/selectChapter";
import CourseType from "../../store/courses/CourseType";
import selectCourseType from "../../store/courses/selectCourseType";
import {useAppSelector} from "../../store/hooks";
import {keyProvider} from "../../store/keyProvider";
import ChapterLabel from "../treeview/ChapterLabel";
import ChapterNavItem from "../treeview/ChapterNavItem";
import ExerciseLabel from "../treeview/ExerciseLabel";
import type LabelProps from "../treeview/LabelProps";
import SectionLabel from "../treeview/SectionLabel";
import AppBarDependentDrawer from "../../utils/AppBarDependentDrawer";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";

const ContentNavHeader = (props: {onClose: () => void}): JSX.Element => {
	const {l10n} = useLocalization();

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			my={1}
			ml={2}
			mr={0.5}
		>
			<Typography variant="h6">
				<Localized id="content-navigation-header-title">
					Table of contents
				</Localized>
			</Typography>
			<IconButtonWithTooltip
				tooltipTitle={
					<Localized id="content-navigation-action-close">
						Close navigation
					</Localized>
				}
				color="primary"
				aria-label={l10n.getString("content-navigation-action-close")}
				onClick={props.onClose}
			>
				<ChevronLeftIcon />
			</IconButtonWithTooltip>
		</Box>
	);
};

function ExpandIcon() {
	return <ExpandMoreIcon color="primary" />;
}

function CollapseIcon() {
	return <ExpandLessIcon color="primary" />;
}

const ContentNavigation = (props: {
	open: boolean;
	courseId: number;
	currentChapterKey: string;
	chapterKeys: string[];
	onClose: () => void;
	width: number;
	mobileMode?: boolean;
}): JSX.Element => {
	const {currentChapterKey} = props;

	const [expanded, setExpanded] = useState([currentChapterKey]);
	const lastExpandedChapter = useRef(currentChapterKey);

	const history = useHistory();
	const theme = useTheme();

	const currentChapter = useAppSelector((state) =>
		selectChapter(state, currentChapterKey)
	);

	const courseType =
		useAppSelector((state) => selectCourseType(state, props.courseId)) ||
		CourseType.Ordinary;

	const {path} = useRouteMatch();

	const match = useRouteMatch<{sectionId?: string}>({
		path: `${path}/sections/:sectionId`,
	});

	const currentSectionKey = useMemo(() => {
		if (!currentChapter?.id || !match?.params.sectionId) {
			return "";
		}

		const sectionId = parseInt(match.params.sectionId);
		if (sectionId) {
			return keyProvider.section(currentChapter.id, sectionId);
		}

		return "";
	}, [currentChapter?.id, match?.params.sectionId]);

	const currentExerciseKey = useMemo(() => {
		if (!currentChapter?.id || history.location.hash === "") {
			return "";
		}

		const exerciseId = hashToExerciseId(history.location.hash);
		if (!exerciseId) {
			return "";
		}

		return keyProvider.chapterExercise(currentChapter.id, exerciseId);
	}, [currentChapter?.id, history.location.hash]);

	const selected = currentExerciseKey || currentSectionKey || currentChapterKey;

	let chapterKeys = props.chapterKeys;
	if (
		currentChapter &&
		requiresExplicitSessionStart(courseType, currentChapter) &&
		currentSectionKey
	) {
		chapterKeys = [currentChapterKey];
	}

	function expandIfNotExpanded(itemId: string) {
		setExpanded((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
	}

	const oneChapter = chapterKeys.length === 1;

	useEffect(() => {
		if (oneChapter) {
			setExpanded([currentChapterKey]);
			lastExpandedChapter.current = currentChapterKey;
		}
	}, [oneChapter, currentChapterKey]);

	const shouldExpandSection = Boolean(
		currentSectionKey && history.location.hash
	);

	useEffect(() => {
		if (shouldExpandSection) {
			expandIfNotExpanded(currentSectionKey);
		}
	}, [currentSectionKey, shouldExpandSection]);

	let width: number | string = props.width;
	if (props.mobileMode) {
		width = `calc(100vw - ${theme.spacing(7)})`;
	}

	function closeIfMobile() {
		if (props.mobileMode) {
			props.onClose();
		}
	}

	const now = new Date();

	return (
		<AppBarDependentDrawer
			open={props.open}
			variant={props.mobileMode ? "temporary" : "persistent"}
			width={width}
			anchor="left"
			topFixedPanel={<ContentNavHeader onClose={props.onClose} />}
			onClose={props.onClose}
		>
			<SimpleTreeView
				expandedItems={expanded}
				selectedItems={selected}
				itemChildrenIndentation={20}
				slots={{
					collapseIcon: CollapseIcon,
					expandIcon: ExpandIcon,
				}}
				onExpandedItemsChange={(_, keys) => {
					if (oneChapter) {
						setExpanded([
							currentChapterKey,
							...keys.filter((k) => k !== currentChapterKey),
						]);

						return;
					}

					let expanded = keys;

					if (
						keys.length > 0 &&
						chapterKeys.includes(keys[0]) &&
						keys[0] !== lastExpandedChapter.current
					) {
						lastExpandedChapter.current = keys[0];
						expanded = [keys[0]];
					}

					setExpanded(expanded);
				}}
				sx={{mt: 1, mb: 2, mx: 1}}
			>
				{chapterKeys.map((key) => (
					<ChapterNavItem
						key={key}
						courseId={props.courseId}
						chapterKey={key}
						collapsible={!oneChapter}
						renderChapterLabel={createChapterLabelRenderer(
							() => getChapterPathByKey(history.location.pathname, key),
							closeIfMobile
						)}
						renderSectionLabel={createSectionLabelRenderer(
							history.location.pathname,
							(sectionKey) => {
								expandIfNotExpanded(sectionKey);
								scrollToTop();
								closeIfMobile();
							}
						)}
						renderExerciseLabel={createExerciseLabelRenderer(
							history.location.pathname,
							history.push,
							closeIfMobile
						)}
						defineItemType={(chapter) =>
							defineChapterNavItemStyle(
								courseType,
								chapter,
								oneChapter,
								key === currentChapterKey && Boolean(currentSectionKey),
								now
							)
						}
						defineSectionItemType={(sectionKey) =>
							defineSectionNavItemStyle(
								sectionKey === currentSectionKey,
								sectionKey === selected
							)
						}
					/>
				))}
			</SimpleTreeView>
		</AppBarDependentDrawer>
	);
};

function defineChapterNavItemStyle(
	courseType: CourseType,
	chapter: Chapter,
	oneChapter: boolean,
	opened: boolean,
	now: Date
) {
	const test = requiresExplicitSessionStart(courseType, chapter);

	return {
		expandable: !test || opened,
		clickable: !oneChapter,
		disabled: chapter.startDate
			? !test && parseDate(chapter.startDate) > now
			: true,
		highlighted: opened && !oneChapter,
	};
}

function defineSectionNavItemStyle(current: boolean, selected: boolean) {
	return {
		highlighted: current && !selected,
	};
}

function createChapterLabelRenderer(
	chapterPath: () => string,
	onClick: () => void
) {
	return function render(
		labelType: "disabled" | "expandable" | "unexpandable",
		params: LabelProps
	) {
		let label = <></>;
		switch (labelType) {
			case "disabled":
			case "expandable":
				label = <ChapterLabel index={params.index} text={params.text} />;

				break;
			case "unexpandable":
				label = (
					<ChapterLabel
						index={params.index}
						text={params.text}
						link={chapterPath()}
						onClick={onClick}
					/>
				);
		}

		return label;
	};
}

function createSectionLabelRenderer(
	pathname: string,
	onClick: (sectionKey: string) => void
) {
	return function render(sectionKey: string, params: LabelProps) {
		const sectionPath = getSectionPathByKey(pathname, sectionKey);

		return (
			<SectionLabel
				index={params.index}
				text={params.text}
				link={sectionPath}
				onClick={(event) => {
					event?.stopPropagation();
					onClick(sectionKey);
				}}
			/>
		);
	};
}

function createExerciseLabelRenderer(
	pathname: string,
	navigateTo: (path: string) => void,
	onClick: () => void
) {
	return function render(
		sectionKey: string,
		exerciseId: number,
		params: LabelProps
	) {
		const sectionPath = getSectionPathByKey(pathname, sectionKey);
		const exercisePath = sectionPath + exerciseIdToHash(exerciseId);

		return (
			<ExerciseLabel
				index={params.index}
				text={params.text}
				link={exercisePath}
				metaIcon={params.metaIcon}
				onClick={(event) => {
					if (event && "key" in event && event.key === "Enter") {
						navigateTo(exercisePath);
					}
					onClick();
				}}
			/>
		);
	};
}

const hashPrefix = "#subsection-";

function exerciseIdToHash(exerciseId: number) {
	return `${hashPrefix}${exerciseId}`;
}

function hashToExerciseId(hash: string) {
	if (!hash.startsWith(hashPrefix)) {
		return 0;
	}

	const n = parseInt(hash.slice(hashPrefix.length));
	return Number.isNaN(n) ? 0 : n;
}

export default ContentNavigation;
